import { TranslateService } from '@ngx-translate/core';
import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import
{
  ColumnReorderEvent, ColumnVisibilityChangeEvent, DataStateChangeEvent, GridComponent,
  GridDataResult
} from '@progress/kendo-angular-grid';
import { StepperComponent } from '@progress/kendo-angular-layout';
import { State } from '@progress/kendo-data-query';
import { GridEventHandler, GridResizeEvent, GridSettings } from 'src/app/core/model/grid-settings';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { GridStatePersistingService } from 'src/app/core/service/grid-state-persisting';
import { PatientImportService } from 'src/app/core/service/patient-import.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { PatientImportEdit } from '../../model/patient-import';
import { SettingService } from 'src/app/core/service/setting.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { GridQueryUtils } from 'src/app/core/utility/grid-query-utils';
import { PatientImportItemListService } from 'src/app/core/service/grid-data/grid-data-patient-import-item.service';
import
{
  SVGIcon,
  checkCircleIcon,
  xOutlineIcon,
} from "@progress/kendo-svg-icons";

@Component({
  standalone: false,
  selector: 'app-patient-import-item-list',
  templateUrl: './patient-import-item-list.component.html',
  // styleUrls: ['./patient-import-item-list.component.scss']
  encapsulation: ViewEncapsulation.None,
  styles: [`
  .k-stepper .k-step-label .k-step-text {
    min-width: 200px;
  }
  .k-stepper .k-step-label {
    display: inline-block;
  }
  `]
})
export class PatientImportItemListComponent implements OnInit
{
  @ViewChild('stepper') public stepper: StepperComponent;

  @Input() public patientImportBatch: PatientImportEdit = null;

  public pageSizes = [5, 10, 25, 50, 100];
  public currentStage = 0;
  public importStages = [];
  public view: GridViewManager;
  public count: number;
  public enableAdvanced = false;
  public showStages = true;
  public parsingError = false;
  public parsingErrorMessage = '';
  public communityEnabled = false;
  public selectedItems: number[] = [];
  public isLocked = false;
  public isSendInvite = false;
  public discarding = false;
  public formGroup: UntypedFormGroup;
  public gridView: GridDataResult;
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: []
  };

  public checkCircleIcon: SVGIcon = checkCircleIcon;
  public closeOutlineIcon: SVGIcon = xOutlineIcon;

  private editedRowIndex: number;
  private items: any[] = [];

  constructor(
    private patientImportService: PatientImportService,
    private appConfigService: AppConfigService,
    private messageHandlerService: MessageHandlerService,
    private translateService: TranslateService,
    private settingService: SettingService,
    private patientImportItemListService: PatientImportItemListService,
    private gridStatePersistingService: GridStatePersistingService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.initGridColumns();
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.patientImportItemListService ? this.patientImportItemListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.importStages = this.patientImportService.patientImportStages;
    this.importStages.forEach(x => { x.isValid = true; });
    if (!this.communityEnabled)
    {
      this.importStages = this.importStages.filter(x => x.value !== 'READY_TO_SEND_INVITE' && x.value !== 'SENDING_INVITE');
    }

    // Load patient import batch id
    this.patientImportService.getPatientImportBatchById(this.patientImportBatch.id).subscribe({
      next: (data: PatientImportEdit) =>
      {
        this.selectedItems = [];
        this.isLocked = data.locked;
        this.isSendInvite = data.sendInvite;

        // Set parsing error
        this.parsingError = data.parsingErrors;
        this.parsingErrorMessage = data.parsingErrorMessage;

        // Set the current import stage
        const stageIndex = this.importStages.findIndex(x => x.value === data.importStage);
        this.currentStage = stageIndex > -1 ? stageIndex : 0;
        if (data.importStage === 'COMPLETED')
        {
          this.currentStage += 1;
        }

        // Set the validation error state and show to the VALIDATED
        if (data.importStage === 'VALIDATED' && data.validationErrors)
        {
          const stageValidatingIndex = this.importStages.findIndex(x => x.value === 'VALIDATING');
          this.importStages[stageValidatingIndex].isValid = false;
        }

        // Load grid data
        this.state = {
          filter: {
            filters: [
              { field: 'subjectImportId', operator: 'eq', value: this.patientImportBatch.id }
            ], logic: 'and'
          }, group: [], skip: 0,
          sort: [
            { field: 'familyName', dir: 'asc' }
          ],
          take: this.appConfigService.getConfig().pageSize
        };
        this.executeQuery();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Reorder grid column order event
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.view.columnsConfig.splice(e.oldIndex - 1, 1);
    this.view.columnsConfig.splice(e.newIndex - 1, 0, ...reorderedColumn);
    this.saveGrid();
  }

  /**
   * Grid onResize event.
   *
   * @param e Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.saveGrid();
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.saveGrid();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.saveGrid();
    this.executeQuery();
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.gridStatePersistingService.delete('grid-settings-subject-import');
    this.initGridColumns();
    this.view.state.filter = { logic: 'and', filters: [] };
  }

  /**
   * Update the selected items and apply discarded updates to selected rows.
   */
  public updateItems(): void
  {
    if (this.selectedItems.length > 0)
    {
      this.discarding = true;
      // Prepare items to update
      this.items = [];
      this.selectedItems.forEach(item =>
      {
        const dataIndex = this.gridView.data.findIndex(x => x.id === item);
        if (dataIndex > -1)
        {
          this.items.push(
            {
              id: this.gridView.data[dataIndex].id,
              discarded: !this.gridView.data[dataIndex].discarded
            });
        }
      });

      // Update batch items
      this.patientImportService.updatePatientImportBatchItems(this.items).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('BATCH-ITEMS-UPDATED-SUCCESSFULLY');
          this.selectedItems = [];
          this.executeQuery();
        },
        error: (errorMessage) =>
        {
          this.messageHandlerService.displayError(errorMessage);
          this.discarding = false;
        },
        complete: () =>
        {
          this.discarding = false;
        }
      });
    }
  }

  /**
   * Edit item handler
   *
   * @param event Grid event
   */
  public editHandler(event: GridEventHandler): void
  {
    this.closeEditor(event.sender);

    // Create the editable form group. All properties editable except id.
    this.formGroup = new UntypedFormGroup(
      {
        id: new UntypedFormControl(event.dataItem.id),
        givenName: new UntypedFormControl(event.dataItem.givenName),
        middleName: new UntypedFormControl(event.dataItem.middleName),
        familyName: new UntypedFormControl(event.dataItem.familyName),
        dateOfBirth: new UntypedFormControl(event.dataItem.dateOfBirth),
        gender: new UntypedFormControl(event.dataItem.gender),
        administrativeGender: new UntypedFormControl(event.dataItem.administrativeGender),
        raceEthnicities: new UntypedFormControl(event.dataItem.raceEthnicities),
        maritalStatus: new UntypedFormControl(event.dataItem.maritalStatus),
        religiousAffiliation: new UntypedFormControl(event.dataItem.religiousAffiliation),
        spokenLanguages: new UntypedFormControl(event.dataItem.spokenLanguages),
        educationLevel: new UntypedFormControl(event.dataItem.educationLevel),
        employmentStatus: new UntypedFormControl(event.dataItem.employmentStatus),
        veteranStatus: new UntypedFormControl(event.dataItem.veteranStatus),
        housingStatus: new UntypedFormControl(event.dataItem.housingStatus),
        householdSize: new UntypedFormControl(event.dataItem.householdSize),
        dependents: new UntypedFormControl(event.dataItem.dependents),
        preferredName: new UntypedFormControl(event.dataItem.preferredName),
        familyIncome: new UntypedFormControl(event.dataItem.familyIncome),
        familyIncomeUnknownDeclined: new UntypedFormControl(event.dataItem.familyIncomeUnknownDeclined),
        authorizedOrganizations: new UntypedFormControl(event.dataItem.authorizedOrganizations),
        language: new UntypedFormControl(event.dataItem.language),
        email: new UntypedFormControl(event.dataItem.email),
        homePhoneNumber: new UntypedFormControl(event.dataItem.homePhoneNumber),
        homePhoneSafe: new UntypedFormControl(event.dataItem.homePhoneSafe),
        cellPhoneNumber: new UntypedFormControl(event.dataItem.cellPhoneNumber),
        cellPhoneSafe: new UntypedFormControl(event.dataItem.cellPhoneSafe),
        workPhoneNumber: new UntypedFormControl(event.dataItem.workPhoneNumber),
        workPhoneNumberExtension: new UntypedFormControl(event.dataItem.workPhoneNumberExtension),
        workPhoneSafe: new UntypedFormControl(event.dataItem.workPhoneSafe),
        otherPhoneNumber: new UntypedFormControl(event.dataItem.otherPhoneNumber),
        otherPhoneSafe: new UntypedFormControl(event.dataItem.otherPhoneSafe),
        streetLine: new UntypedFormControl(event.dataItem.streetLine),
        streetLineAdditional: new UntypedFormControl(event.dataItem.streetLineAdditional),
        city: new UntypedFormControl(event.dataItem.city),
        county: new UntypedFormControl(event.dataItem.county),
        state: new UntypedFormControl(event.dataItem.state),
        postalCode: new UntypedFormControl(event.dataItem.postalCode),
        country: new UntypedFormControl(event.dataItem.country),
        identifiers: new UntypedFormControl(event.dataItem.identifiers),
        extensions: new UntypedFormControl(event.dataItem.extensions),
        manager: new UntypedFormControl(event.dataItem.manager),
        caseExtensions: new UntypedFormControl(event.dataItem.caseExtensions),
        discarded: new UntypedFormControl(event.dataItem.discarded),
        subjectNotes: new UntypedFormControl(event.dataItem.subjectNotes),
        caseNotes: new UntypedFormControl(event.dataItem.caseNotes),
        subjectFlags: new UntypedFormControl(event.dataItem.subjectFlags),
        caseFlags: new UntypedFormControl(event.dataItem.caseFlags),
        namePronoun: new UntypedFormControl(event.dataItem.namePronoun),
        namePronounOther: new UntypedFormControl(event.dataItem.namePronounOther),
        interpreterNeed: new UntypedFormControl(event.dataItem.interpreterNeed),
        interpreterNeedOther: new UntypedFormControl(event.dataItem.interpreterNeedOther),
        englishProficiency: new UntypedFormControl(event.dataItem.englishProficiency),
        races: new UntypedFormControl(event.dataItem.races),
        sexualOrientation: new UntypedFormControl(event.dataItem.sexualOrientation),
        livesAlone: new UntypedFormControl(event.dataItem.livesAlone),
        primaryCaregiver: new UntypedFormControl(event.dataItem.primaryCaregiver),
        tribalAffiliated: new UntypedFormControl(event.dataItem.tribalAffiliated),
        tribalAffiliations: new UntypedFormControl(event.dataItem.tribalAffiliations),
        tribalEnrolled: new UntypedFormControl(event.dataItem.tribalEnrolled),
        tribalEnrollments: new UntypedFormControl(event.dataItem.tribalEnrollments),
        consents: new UntypedFormControl(event.dataItem.consents),
        caseSensitivities: new UntypedFormControl(event.dataItem.caseSensitivities),
        communityService: new UntypedFormControl(event.dataItem.communityService),
        addresses: new UntypedFormControl(event.dataItem.addresses),
        memberChildren: new UntypedFormControl(event.dataItem.memberChildren),
        memberAdults: new UntypedFormControl(event.dataItem.memberAdults),
        enhancedServices: new UntypedFormControl(event.dataItem.enhancedServices),
        pagerNumber: new UntypedFormControl(event.dataItem.pagerNumber),
        pagerSafe: new UntypedFormControl(event.dataItem.pagerSafe),
        faxNumber: new UntypedFormControl(event.dataItem.faxNumber),
        faxSafe: new UntypedFormControl(event.dataItem.faxSafe),
        url: new UntypedFormControl(event.dataItem.url),

        // PCP
        pcpIdentifiers: new UntypedFormControl(event.dataItem.pcpIdentifiers),
        pcpTitle: new UntypedFormControl(event.dataItem.pcpTitle),
        pcpSuffix: new UntypedFormControl(event.dataItem.pcpSuffix),
        pcpProfessionalSuffix: new UntypedFormControl(event.dataItem.pcpProfessionalSuffix),
        pcpSpokenLanguages: new UntypedFormControl(event.dataItem.pcpSpokenLanguages),
        pcpOrganization: new UntypedFormControl(event.dataItem.pcpOrganization),
        pcpGivenName: new UntypedFormControl(event.dataItem.pcpGivenName),
        pcpMiddleName: new UntypedFormControl(event.dataItem.pcpMiddleName),
        pcpFamilyName: new UntypedFormControl(event.dataItem.pcpFamilyName),
        pcpGender: new UntypedFormControl(event.dataItem.pcpGender),
        pcpEmail: new UntypedFormControl(event.dataItem.pcpEmail),
        pcpHomePhoneNumber: new UntypedFormControl(event.dataItem.pcpHomePhoneNumber),
        pcpHomePhoneNumberExtension: new UntypedFormControl(event.dataItem.pcpHomePhoneNumberExtension),
        pcpCellPhoneNumber: new UntypedFormControl(event.dataItem.pcpCellPhoneNumber),
        pcpWorkPhoneNumber: new UntypedFormControl(event.dataItem.pcpWorkPhoneNumber),
        pcpWorkPhoneNumberExtension: new UntypedFormControl(event.dataItem.pcpWorkPhoneNumberExtension),
        pcpOtherPhoneNumber: new UntypedFormControl(event.dataItem.pcpOtherPhoneNumber),
        pcpStreetLine: new UntypedFormControl(event.dataItem.pcpStreetLine),
        pcpStreetLineAdditional: new UntypedFormControl(event.dataItem.pcpStreetLineAdditional),
        pcpCity: new UntypedFormControl(event.dataItem.pcpCity),
        pcpCounty: new UntypedFormControl(event.dataItem.pcpCounty),
        pcpState: new UntypedFormControl(event.dataItem.pcpState),
        pcpPostalCode: new UntypedFormControl(event.dataItem.pcpPostalCode),
        pcpCountry: new UntypedFormControl(event.dataItem.pcpCountry),
        pcpRole: new UntypedFormControl(event.dataItem.pcpRole),
        pcpSubRole: new UntypedFormControl(event.dataItem.pcpSubRole),
        pcpTaxNo: new UntypedFormControl(event.dataItem.pcpTaxNo),
        pcpExtensions: new UntypedFormControl(event.dataItem.pcpExtensions),
        pcpDescription: new UntypedFormControl(event.dataItem.pcpDescription),
        pcpFrom: new UntypedFormControl(event.dataItem.pcpFrom),
        pcpTo: new UntypedFormControl(event.dataItem.pcpTo),
        // Contact
        contactIdentifiers: new UntypedFormControl(event.dataItem.contactIdentifiers),
        contactTitle: new UntypedFormControl(event.dataItem.contactTitle),
        contactSuffix: new UntypedFormControl(event.dataItem.contactSuffix),
        contactProfessionalSuffix: new UntypedFormControl(event.dataItem.contactProfessionalSuffix),
        contactSpokenLanguages: new UntypedFormControl(event.dataItem.contactSpokenLanguages),
        contactGivenName: new UntypedFormControl(event.dataItem.contactGivenName),
        contactMiddleName: new UntypedFormControl(event.dataItem.contactMiddleName),
        contactFamilyName: new UntypedFormControl(event.dataItem.contactFamilyName),
        contactGender: new UntypedFormControl(event.dataItem.contactGender),
        contactEmail: new UntypedFormControl(event.dataItem.contactEmail),
        contactHomePhoneNumber: new UntypedFormControl(event.dataItem.contactHomePhoneNumber),
        contactHomePhoneNumberExtension: new UntypedFormControl(event.dataItem.contactHomePhoneNumberExtension),
        contactCellPhoneNumber: new UntypedFormControl(event.dataItem.contactCellPhoneNumber),
        contactWorkPhoneNumber: new UntypedFormControl(event.dataItem.contactWorkPhoneNumber),
        contactWorkPhoneNumberExtension: new UntypedFormControl(event.dataItem.contactWorkPhoneNumberExtension),
        contactOtherPhoneNumber: new UntypedFormControl(event.dataItem.contactOtherPhoneNumber),
        contactStreetLine: new UntypedFormControl(event.dataItem.contactStreetLine),
        contactStreetLineAdditional: new UntypedFormControl(event.dataItem.contactStreetLineAdditional),
        contactCity: new UntypedFormControl(event.dataItem.contactCity),
        contactCounty: new UntypedFormControl(event.dataItem.contactCounty),
        contactState: new UntypedFormControl(event.dataItem.contactState),
        contactPostalCode: new UntypedFormControl(event.dataItem.contactPostalCode),
        contactCountry: new UntypedFormControl(event.dataItem.contactCountry),
        contactRole: new UntypedFormControl(event.dataItem.contactRole),
        contactSubRole: new UntypedFormControl(event.dataItem.contactSubRole),
        contactExtensions: new UntypedFormControl(event.dataItem.contactExtensions),
        contactDescription: new UntypedFormControl(event.dataItem.contactDescription),
        contactFrom: new UntypedFormControl(event.dataItem.contactFrom),
        contactTo: new UntypedFormControl(event.dataItem.contactTo),
        // Insurance
        insurerName: new UntypedFormControl(event.dataItem.insurerName),
        insuranceSubjectId: new UntypedFormControl(event.dataItem.insuranceSubjectId),
        insurancePlanId: new UntypedFormControl(event.dataItem.insurancePlanId),
        insuranceGroupId: new UntypedFormControl(event.dataItem.insuranceGroupId),
        insuranceStartDate: new UntypedFormControl(event.dataItem.insuranceStartDate),
        insuranceEndDate: new UntypedFormControl(event.dataItem.insuranceEndDate),
        insuredFirstName: new UntypedFormControl(event.dataItem.insuredFirstName),
        insuredLastName: new UntypedFormControl(event.dataItem.insuredLastName),
        policyHolder: new UntypedFormControl(event.dataItem.policyHolder),
        insurerPhoneNumber: new UntypedFormControl(event.dataItem.insurerPhoneNumber),
        insurerWebSite: new UntypedFormControl(event.dataItem.insurerWebSite),
        employer: new UntypedFormControl(event.dataItem.employer),
        insuranceExternalId: new UntypedFormControl(event.dataItem.insuranceExternalId),
      }
    );
    this.editedRowIndex = event.rowIndex;
    event.sender.editRow(event.rowIndex, this.formGroup);
  }

  /**
   * Cancel edit handler
   */
  public cancelHandler(event: GridEventHandler): void
  {
    this.closeEditor(event.sender, event.rowIndex);
  }

  /**
   * Save item handler
   */
  public saveHandler(event: GridEventHandler): void
  {
    const item = event.formGroup.value;

    this.patientImportService.updatePatientImportBatchItem(item).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('BATCH-ITEM-UPDATED-SUCCESSFULLY');
        this.executeQuery();
      },
      error: (errorMessage) =>
      {
        this.messageHandlerService.displayError(errorMessage);
      }
    });
    event.sender.closeRow(event.rowIndex);
  }

  public onSelectedKeysChange(e: any): void
  {
    this.selectedItems = e;
  }

  /**
   * Close editor handler
   *
   * @param grid Item grid
   * @param rowIndex Index on the selected row
   */
  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex)
  {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  /**
   * Execute patient import list query
   */
  private executeQuery(): void
  {
    const httpPostBody = GridQueryUtils.normalizeFilters(this.state);
    this.patientImportItemListService.query((items) =>
    {
      items.data.forEach(d =>
      {
        d.processedDate = d.processedDate !== null ? new Date(d.processedDate) : null;
        d.processedDateSentInvite = d.processedDateSentInvite !== null ? new Date(d.processedDateSentInvite) : null;
        d.lastModifiedOn = new Date(d.lastModifiedOn);
        d.createdOn = new Date(d.createdOn);
      });

      this.count = items.total;
      this.gridView = items;
    }, '', null, null, 'post', httpPostBody);
  }

  /**
   * Grid columns initialization
   */
  private initGridColumns(): void
  {
    this.translateService.get(['FIRST-NAME', 'LAST-NAME', 'MIDDLE-NAME', 'GENDER', 'ADMINISTRATIVE-GENDER', 'EMAIL', 'DATE-OF-BIRTH',
      'HOME-PHONE', 'CELL-PHONE', 'WORK-PHONE-LABEL', 'WORK-PHONE-EXTENSION', 'OTHER-PHONE-LABEL',
      'STREET', 'ADDRESS-LINE-2', 'CITY', 'STATE', 'POSTAL-CODE', 'COUNTRY',
      'IS-VALID', 'IMPORT-MESSAGES', 'DISCARDED',
      'IMPORT-PROCESSED-DATE', 'IMPORT-PROCESSED-DATE-SENT-INVITE', 'CASE-MANAGER', 'ID',
      'CREATED-BY', 'CREATED-DATE', 'LAST-MODIFIED-BY', 'LAST-MODIFIED-DATE',
      'PATIENT-IMPORT-TYPE-SUBJECT', 'PATIENT-IMPORT-TYPE-PCP', 'PATIENT-IMPORT-TYPE-CONTACT',
      'PATIENT-IMPORT-IDENTIFIERS',
      'PATIENT-IMPORT-EXTENSIONS',
      'PATIENT-IMPORT-TITLE',
      'PATIENT-IMPORT-TAX-NO',
      'PATIENT-IMPORT-FIRST-NAME',
      'PATIENT-IMPORT-LAST-NAME',
      'PATIENT-IMPORT-MIDDLE-NAME',
      'PATIENT-IMPORT-GENDER',
      'PATIENT-IMPORT-EMAIL',
      'PATIENT-IMPORT-OFFICE-PHONE',
      'PATIENT-IMPORT-OFFICE-PHONE-EXTENSION',
      'PATIENT-IMPORT-HOME-PHONE',
      'PATIENT-IMPORT-HOME-PHONE-EXTENSION',
      'PATIENT-IMPORT-CELL-PHONE',
      'PATIENT-IMPORT-WORK-PHONE',
      'PATIENT-IMPORT-WORK-PHONE-EXTENSION',
      'PATIENT-IMPORT-OTHER-PHONE',
      'PATIENT-IMPORT-PAGER',
      'PATIENT-IMPORT-FAX',
      'PATIENT-IMPORT-URL',
      'PATIENT-IMPORT-STREET',
      'PATIENT-IMPORT-ADDRESS-LINE-2',
      'PATIENT-IMPORT-CITY',
      'PATIENT-IMPORT-STATE',
      'PATIENT-IMPORT-POSTAL-CODE',
      'PATIENT-IMPORT-COUNTRY',
      'PATIENT-IMPORT-ROLE',
      'PATIENT-IMPORT-SUB-ROLE',
      'PATIENT-IMPORT-COUNTY',
      'COUNTY',
      'NOTIFICATION-LANGUAGE',
      'PATIENT-IMPORT-INSURER-NAME',
      'PATIENT-IMPORT-INSURANCE-SUBJECT-ID',
      'PATIENT-IMPORT-INSURANCE-PLAN-ID',
      'PATIENT-IMPORT-INSURANCE-GROUP-ID',
      'PATIENT-IMPORT-INSURANCE-START-DATE',
      'PATIENT-IMPORT-INSURANCE-END-DATE',
      'PATIENT-IMPORT-INSURED-FIRST-NAME',
      'PATIENT-IMPORT-INSURED-LAST-NAME',
      'PATIENT-IMPORT-POLICY-HOLDER',
      'PATIENT-IMPORT-INSURER-PHONE-NUMBER',
      'PATIENT-IMPORT-INSURER-WEBSITE',
      'PATIENT-IMPORT-EMPLOYER',
      'PATIENT-IMPORT-INSURANCE-EXTERNAL-ID',
      'PATIENT-IMPORT-ORGANIZATION',
      'PATIENT-IMPORT-RACE-ETHNICITY',
      'PATIENT-IMPORT-MARITAL-STATUS',
      'PATIENT-IMPORT-RELIGIOUS-AFFILIATION',
      'PATIENT-IMPORT-EDUCATION-LEVEL',
      'PATIENT-IMPORT-EMPLOYMENT-STATUS',
      'PATIENT-IMPORT-HOUSING-STATUS',
      'PATIENT-IMPORT-HOUSEHOLD-SIZE',
      'PATIENT-IMPORT-FAMILY-INCOME',
      'PATIENT-IMPORT-FAMILY-INCOME-UNKNOWN-DECLINED',
      'PATIENT-IMPORT-CASE-EXTENSIONS',
      'PATIENT-IMPORT-VETERAN-STATUS',
      'PATIENT-IMPORT-PREFERRED-NAME',
      'PATIENT-IMPORT-PATIENT-NOTES',
      'PATIENT-IMPORT-CASE-NOTES',
      'PATIENT-IMPORT-PATIENT-FLAGS',
      'PATIENT-IMPORT-CASE-FLAGS',
      'SPOKEN-LANGUAGES',
      'WRITTEN-LANGUAGES',
      'READING-LANGUAGES',
      'LIVES-ALONE',
      'RACES',
      'SEXUAL-ORIENTATION',
      'NAME-PRONOUN',
      'NAME-PRONOUN-OTHER',
      'ENGLISH-PROFICIENCY',
      'INTERPRETER-NEED',
      'INTERPRETER-NEED-OTHER',
      'TRIBAL-AFFILIATED',
      'TRIBAL-AFFILIATIONS',
      'TRIBAL-ENROLLED',
      'TRIBAL-ENROLLMENTS',
      'CONSENTS',
      'CASE-SENSITIVITIES',
      'COMMUNITY-SERVICE',
      'ADDRESSES',
      'PATIENT-IMPORT-SUFFIX',
      'PATIENT-IMPORT-PROFESSIONAL-SUFFIX',
      'PATIENT-IMPORT-SPOKEN-LANGUAGES',
      'PATIENT-IMPORT-DESCRIPTION',
      'PATIENT-IMPORT-FROM',
      'PATIENT-IMPORT-TO',
      'HOME-PHONE-SAFE',
      'CELL-PHONE-SAFE',
      'WORK-PHONE-SAFE',
      'OTHER-PHONE-SAFE',
      'PAGER-SAFE',
      'FAX-SAFE',
      'TOTAL-CHILDREN',
      'TOTAL-ADULTS',
      'PAGER',
      'FAX-OR-OTHER',
      'URL',
      'ENHANCED-SERVICE-ELIGIBILITY',
      'DEPENDENTS',
      'PREFERRED-CONTACT-METHODS',
      'PRIMARY-CAREGIVER',
      'MEMBER-RESTRICTIONS',
    ]).subscribe({
      next: (res) =>
      {
        this.view.columnsConfig = [
          { field: 'id', title: res.ID, titleKeys: ['ID'], hidden: true },
          { field: 'givenName', title: res['FIRST-NAME'], titleKeys: ['FIRST-NAME'], hidden: false },
          { field: 'middleName', title: res['MIDDLE-NAME'], titleKeys: ['MIDDLE-NAME'], hidden: true },
          { field: 'familyName', title: res['LAST-NAME'], titleKeys: ['LAST-NAME'], hidden: false },
          { field: 'dateOfBirth', title: res['DATE-OF-BIRTH'], titleKeys: ['DATE-OF-BIRTH'], hidden: false },
          { field: 'gender', title: res.GENDER, titleKeys: ['GENDER'], hidden: true },
          { field: 'administrativeGender', title: res['ADMINISTRATIVE-GENDER'], titleKeys: ['ADMINISTRATIVE-GENDER'], hidden: true },
          { field: 'language', title: res['NOTIFICATION-LANGUAGE'], titleKeys: ['NOTIFICATION-LANGUAGE'], hidden: true },
          {
            field: 'raceEthnicities', title: res['PATIENT-IMPORT-RACE-ETHNICITY'],
            titleKeys: ['PATIENT-IMPORT-RACE-ETHNICITY'], hidden: true
          },
          {
            field: 'maritalStatus', title: res['PATIENT-IMPORT-MARITAL-STATUS'],
            titleKeys: ['PATIENT-IMPORT-MARITAL-STATUS'], hidden: true
          },
          {
            field: 'religiousAffiliation', title: res['PATIENT-IMPORT-RELIGIOUS-AFFILIATION'],
            titleKeys: ['PATIENT-IMPORT-RELIGIOUS-AFFILIATION'], hidden: true
          },
          { field: 'spokenLanguages', title: res['SPOKEN-LANGUAGES'], titleKeys: ['SPOKEN-LANGUAGES'], hidden: true },
          { field: 'writtenLanguages', title: res['WRITTEN-LANGUAGES'], titleKeys: ['WRITTEN-LANGUAGES'], hidden: true },
          { field: 'readingLanguages', title: res['READING-LANGUAGES'], titleKeys: ['READING-LANGUAGES'], hidden: true },
          {
            field: 'educationLevel', title: res['PATIENT-IMPORT-EDUCATION-LEVEL'], titleKeys: ['PATIENT-IMPORT-EDUCATION-LEVEL'],
            hidden: true
          },
          {
            field: 'employmentStatus', title: res['PATIENT-IMPORT-EMPLOYMENT-STATUS'], titleKeys: ['PATIENT-IMPORT-EMPLOYMENT-STATUS'],
            hidden: true
          },
          {
            field: 'veteranStatus', title: res['PATIENT-IMPORT-VETERAN-STATUS'], titleKeys: ['PATIENT-IMPORT-VETERAN-STATUS'], hidden: true
          },
          {
            field: 'housingStatus', title: res['PATIENT-IMPORT-HOUSING-STATUS'], titleKeys: ['PATIENT-IMPORT-HOUSING-STATUS'], hidden: true
          },
          {
            field: 'householdSize', title: res['PATIENT-IMPORT-HOUSEHOLD-SIZE'], titleKeys: ['PATIENT-IMPORT-HOUSEHOLD-SIZE'], hidden: true
          },
          {
            field: 'dependents', title: res.DEPENDENTS, titleKeys: ['DEPENDENTS'], hidden: true
          },
          {
            field: 'preferredName', title: res['PATIENT-IMPORT-PREFERRED-NAME'], titleKeys: ['PATIENT-IMPORT-PREFERRED-NAME'], hidden: true
          },
          {
            field: 'preferredContactMethods', title: res['PREFERRED-CONTACT-METHODS'], titleKeys: ['PREFERRED-CONTACT-METHODS'], hidden: true
          },
          { field: 'familyIncome', title: res['PATIENT-IMPORT-FAMILY-INCOME'], titleKeys: ['PATIENT-IMPORT-FAMILY-INCOME'], hidden: true },
          { field: 'familyIncomeUnknownDeclined', title: res['PATIENT-IMPORT-FAMILY-INCOME-UNKNOWN-DECLINED'], titleKeys: ['PATIENT-IMPORT-FAMILY-INCOME-UNKNOWN-DECLINED'], hidden: true },          
          { field: 'email', title: res.EMAIL, titleKeys: ['EMAIL'], hidden: true },
          { field: 'homePhoneNumber', title: res['HOME-PHONE'], titleKeys: ['HOME-PHONE'], hidden: true },
          { field: 'homePhoneSafe', title: res['HOME-PHONE-SAFE'], titleKeys: ['HOME-PHONE-SAFE'], hidden: true },
          { field: 'cellPhoneNumber', title: res['CELL-PHONE'], titleKeys: ['CELL-PHONE'], hidden: true },
          { field: 'cellPhoneSafe', title: res['CELL-PHONE-SAFE'], titleKeys: ['CELL-PHONE-SAFE'], hidden: true },
          { field: 'otherPhoneNumber', title: res['OTHER-PHONE-LABEL'], titleKeys: ['OTHER-PHONE-LABEL'], hidden: true },
          { field: 'otherPhoneSafe', title: res['OTHER-PHONE-SAFE'], titleKeys: ['OTHER-PHONE-SAFE'], hidden: true },
          { field: 'workPhoneNumber', title: res['WORK-PHONE-LABEL'], titleKeys: ['WORK-PHONE-LABEL'], hidden: true },
          { field: 'workPhoneNumberExtension', title: res['WORK-PHONE-EXTENSION'], titleKeys: ['WORK-PHONE-EXTENSION'], hidden: true },
          { field: 'workPhoneSafe', title: res['WORK-PHONE-SAFE'], titleKeys: ['WORK-PHONE-SAFE'], hidden: true },
          { field: 'pagerNumber', title: res.PAGER, titleKeys: ['PAGER'], hidden: true },
          { field: 'pagerSafe', title: res['PAGER-SAFE'], titleKeys: ['PAGER-SAFE'], hidden: true },
          { field: 'faxNumber', title: res['FAX-OR-OTHER'], titleKeys: ['FAX-OR-OTHER'], hidden: true },
          { field: 'faxSafe', title: res['FAX-SAFE'], titleKeys: ['FAX-SAFE'], hidden: true },
          { field: 'url', title: res.URL, titleKeys: ['URL'], hidden: true },
          { field: 'streetLine', title: res.STREET, titleKeys: ['STREET'], hidden: true },
          { field: 'streetLineAdditional', title: res['ADDRESS-LINE-2'], titleKeys: ['ADDRESS-LINE-2'], hidden: true },
          { field: 'city', title: res.CITY, titleKeys: ['CITY'], hidden: true },
          { field: 'county', title: res.COUNTY, titleKeys: ['COUNTY'], hidden: true },
          { field: 'state', title: res.STATE, titleKeys: ['STATE'], hidden: true },
          { field: 'postalCode', title: res['POSTAL-CODE'], titleKeys: ['POSTAL-CODE'], hidden: true },
          { field: 'country', title: res.COUNTRY, titleKeys: ['COUNTRY'], hidden: true },
          {
            field: 'identifiers', title: res['PATIENT-IMPORT-IDENTIFIERS'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-SUBJECT']),
            titleKeys: ['PATIENT-IMPORT-IDENTIFIERS', 'PATIENT-IMPORT-TYPE-SUBJECT'], hidden: true
          },
          {
            field: 'extensions', title: res['PATIENT-IMPORT-EXTENSIONS'].replace('{type}', res['PATIENT-IMPORT-TYPE-SUBJECT']),
            titleKeys: ['PATIENT-IMPORT-EXTENSIONS', 'PATIENT-IMPORT-TYPE-SUBJECT'], hidden: true
          },
          { field: 'manager', title: res['CASE-MANAGER'], titleKeys: ['CASE-MANAGER'], hidden: true },
          {
            field: 'caseExtensions', title: res['PATIENT-IMPORT-CASE-EXTENSIONS'],
            titleKeys: ['PATIENT-IMPORT-CASE-EXTENSIONS'], hidden: true
          },
          { field: 'authorizedOrganizations', title: res['MEMBER-RESTRICTIONS'], titleKeys: ['MEMBER-RESTRICTIONS'], hidden: true },          
          { field: 'discarded', title: res.DISCARDED, titleKeys: ['DISCARDED'], filter: 'boolean', editor: 'boolean', hidden: false },
          { field: 'valid', title: res['IS-VALID'], titleKeys: ['IS-VALID'], filter: 'boolean', hidden: false },
          { field: 'errorMessage', title: res['IMPORT-MESSAGES'], titleKeys: ['IMPORT-MESSAGES'], hidden: false },
          {
            field: 'processedDate', title: res['IMPORT-PROCESSED-DATE'], titleKeys: ['IMPORT-PROCESSED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date'
          },
          {
            field: 'processedDateSentInvite', title: res['IMPORT-PROCESSED-DATE-SENT-INVITE'],
            titleKeys: ['IMPORT-PROCESSED-DATE-SENT-INVITE'], hidden: true, format: '{0:g}', filter: 'date'
          },
          { field: 'createdBy', title: res['CREATED-BY'], titleKeys: ['CREATED-BY'], hidden: true },
          {
            field: 'createdOn', title: res['CREATED-DATE'], titleKeys: ['CREATED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date'
          },
          { field: 'lastModifiedBy', title: res['LAST-MODIFIED-BY'], titleKeys: ['LAST-MODIFIED-BY'], hidden: true },
          {
            field: 'lastModifiedOn', title: res['LAST-MODIFIED-DATE'], titleKeys: ['LAST-MODIFIED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date'
          },
          { field: 'subjectNotes', title: res['PATIENT-IMPORT-PATIENT-NOTES'], titleKeys: ['PATIENT-IMPORT-PATIENT-NOTES'], hidden: true },
          { field: 'caseNotes', title: res['PATIENT-IMPORT-CASE-NOTES'], titleKeys: ['PATIENT-IMPORT-CASE-NOTES'], hidden: true },
          { field: 'subjectFlags', title: res['PATIENT-IMPORT-PATIENT-FLAGS'], titleKeys: ['PATIENT-IMPORT-PATIENT-FLAGS'], hidden: true },
          { field: 'caseFlags', title: res['PATIENT-IMPORT-CASE-FLAGS'], titleKeys: ['PATIENT-IMPORT-CASE-FLAGS'], hidden: true },
          { field: 'namePronoun', title: res['NAME-PRONOUN'], titleKeys: ['NAME-PRONOUN'], hidden: true },
          { field: 'namePronounOther', title: res['NAME-PRONOUN-OTHER'], titleKeys: ['NAME-PRONOUN-OTHER'], hidden: true },
          { field: 'interpreterNeed', title: res['INTERPRETER-NEED'], titleKeys: ['INTERPRETER-NEED'], hidden: true },
          { field: 'interpreterNeedOther', title: res['INTERPRETER-NEED-OTHER'], titleKeys: ['INTERPRETER-NEED-OTHER'], hidden: true },
          { field: 'englishProficiency', title: res['ENGLISH-PROFICIENCY'], titleKeys: ['ENGLISH-PROFICIENCY'], hidden: true },
          { field: 'races', title: res.RACES, titleKeys: ['RACES'], hidden: true },
          { field: 'sexualOrientation', title: res['SEXUAL-ORIENTATION'], titleKeys: ['SEXUAL-ORIENTATION'], hidden: true },
          { field: 'livesAlone', title: res['LIVES-ALONE'], titleKeys: ['LIVES-ALONE'], hidden: true },
          { field: 'primaryCaregiver', title: res['PRIMARY-CAREGIVER'], titleKeys: ['PRIMARY-CAREGIVER'], hidden: true },
          { field: 'tribalAffiliated', title: res['TRIBAL-AFFILIATED'], titleKeys: ['TRIBAL-AFFILIATED'], hidden: true },
          { field: 'tribalAffiliations', title: res['TRIBAL-AFFILIATIONS'], titleKeys: ['TRIBAL-AFFILIATIONS'], hidden: true },
          { field: 'tribalEnrolled', title: res['TRIBAL-ENROLLED'], titleKeys: ['TRIBAL-ENROLLED'], hidden: true },
          { field: 'tribalEnrollments', title: res['TRIBAL-ENROLLMENTS'], titleKeys: ['TRIBAL-ENROLLMENTS'], hidden: true },
          { field: 'consents', title: res.CONSENTS, titleKeys: ['CONSENTS'], hidden: true },
          { field: 'caseSensitivities', title: res['CASE-SENSITIVITIES'], titleKeys: ['CASE-SENSITIVITIES'], hidden: true },
          { field: 'communityService', title: res['COMMUNITY-SERVICE'], titleKeys: ['COMMUNITY-SERVICE'], hidden: true },
          { field: 'addresses', title: res.ADDRESSES, titleKeys: ['ADDRESSES'], hidden: true },
          { field: 'memberChildren', title: res['TOTAL-CHILDREN'], titleKeys: ['TOTAL-CHILDREN'], hidden: true },
          { field: 'memberAdults', title: res['TOTAL-ADULTS'], titleKeys: ['TOTAL-ADULTS'], hidden: true },
          { field: 'enhancedServices', title: res['ENHANCED-SERVICE-ELIGIBILITY'], titleKeys: ['ENHANCED-SERVICE-ELIGIBILITY'], hidden: true },

          /*PCP*/
          {
            field: 'pcpIdentifiers', title: res['PATIENT-IMPORT-IDENTIFIERS'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-IDENTIFIERS', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpTitle', title: res['PATIENT-IMPORT-TITLE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-TITLE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpSuffix', title: res['PATIENT-IMPORT-SUFFIX'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-SUFFIX', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpProfessionalSuffix', title: res['PATIENT-IMPORT-PROFESSIONAL-SUFFIX'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-PROFESSIONAL-SUFFIX', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpSpokenLanguages', title: res['PATIENT-IMPORT-SPOKEN-LANGUAGES'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-SPOKEN-LANGUAGES', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpOrganization', title: res['PATIENT-IMPORT-ORGANIZATION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-ORGANIZATION', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpGivenName', title: res['PATIENT-IMPORT-FIRST-NAME'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-FIRST-NAME', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpFamilyName', title: res['PATIENT-IMPORT-LAST-NAME'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-LAST-NAME', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpMiddleName', title: res['PATIENT-IMPORT-MIDDLE-NAME'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-MIDDLE-NAME', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpGender', title: res['PATIENT-IMPORT-GENDER'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-GENDER', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpEmail', title: res['PATIENT-IMPORT-EMAIL'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-EMAIL', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpHomePhoneNumber', title: res['PATIENT-IMPORT-OFFICE-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-OFFICE-PHONE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpHomePhoneNumberExtension', title: res['PATIENT-IMPORT-OFFICE-PHONE-EXTENSION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-OFFICE-PHONE-EXTENSION', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpCellPhoneNumber', title: res['PATIENT-IMPORT-CELL-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-CELL-PHONE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpWorkPhoneNumber', title: res['PATIENT-IMPORT-WORK-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-WORK-PHONE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpWorkPhoneNumberExtenstion', title: res['PATIENT-IMPORT-WORK-PHONE-EXTENSION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-WORK-PHONE-EXTENSION', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpOtherPhoneNumber', title: res['PATIENT-IMPORT-OTHER-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-OTHER-PHONE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpStreetLine', title: res['PATIENT-IMPORT-STREET'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-STREET', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpStreetLineAdditional', title: res['PATIENT-IMPORT-ADDRESS-LINE-2'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-ADDRESS-LINE-2', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpCity', title: res['PATIENT-IMPORT-CITY'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-CITY', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpCounty', title: res['PATIENT-IMPORT-COUNTY'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-COUNTY', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpState', title: res['PATIENT-IMPORT-STATE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-STATE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpPostalCode', title: res['PATIENT-IMPORT-POSTAL-CODE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-POSTAL-CODE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpCountry', title: res['PATIENT-IMPORT-COUNTRY'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-COUNTRY', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpRole', title: res['PATIENT-IMPORT-ROLE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-ROLE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpSubRole', title: res['PATIENT-IMPORT-SUB-ROLE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-SUB-ROLE', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpTaxNo', title: res['PATIENT-IMPORT-TAX-NO'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-TAX-NO', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpExtensions', title: res['PATIENT-IMPORT-EXTENSIONS'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-EXTENSIONS', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpDescription', title: res['PATIENT-IMPORT-DESCRIPTION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-DESCRIPTION', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpFrom', title: res['PATIENT-IMPORT-FROM'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-FROM', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          {
            field: 'pcpTo', title: res['PATIENT-IMPORT-TO'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-PCP']), titleKeys: ['PATIENT-IMPORT-TO', 'PATIENT-IMPORT-TYPE-PCP'], hidden: true
          },
          /*Contact*/
          {
            field: 'contactIdentifiers', title: res['PATIENT-IMPORT-IDENTIFIERS'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-IDENTIFIERS', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactTitle', title: res['PATIENT-IMPORT-TITLE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-TITLE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactSuffix', title: res['PATIENT-IMPORT-SUFFIX'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-SUFFIX', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactProfessionalSuffix', title: res['PATIENT-IMPORT-PROFESSIONAL-SUFFIX'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-PROFESSIONAL-SUFFIX', 'PATIENT-IMPORT-TYPE-CONTACT'],
            hidden: true
          },
          {
            field: 'contactSpokenLanguages', title: res['PATIENT-IMPORT-SPOKEN-LANGUAGES'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-SPOKEN-LANGUAGES', 'PATIENT-IMPORT-TYPE-CONTACT'],
            hidden: true
          },
          {
            field: 'contactGivenName', title: res['PATIENT-IMPORT-FIRST-NAME'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-FIRST-NAME', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactFamilyName', title: res['PATIENT-IMPORT-LAST-NAME'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-LAST-NAME', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactMiddleName', title: res['PATIENT-IMPORT-MIDDLE-NAME'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-MIDDLE-NAME', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactGender', title: res['PATIENT-IMPORT-GENDER'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-GENDER', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactEmail', title: res['PATIENT-IMPORT-EMAIL'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-EMAIL', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactHomePhoneNumber', title: res['PATIENT-IMPORT-HOME-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-HOME-PHONE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactHomePhoneNumberExtension', title: res['PATIENT-IMPORT-HOME-PHONE-EXTENSION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-HOME-PHONE-EXTENSION', 'PATIENT-IMPORT-TYPE-CONTACT'],
            hidden: true
          },
          {
            field: 'contactCellPhoneNumber', title: res['PATIENT-IMPORT-CELL-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-CELL-PHONE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactWorkPhoneNumber', title: res['PATIENT-IMPORT-WORK-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-WORK-PHONE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactWorkPhoneNumberExtension', title: res['PATIENT-IMPORT-WORK-PHONE-EXTENSION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-WORK-PHONE-EXTENSION', 'PATIENT-IMPORT-TYPE-CONTACT'],
            hidden: true
          },
          {
            field: 'contactOtherPhoneNumber', title: res['PATIENT-IMPORT-OTHER-PHONE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-OTHER-PHONE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactStreetLine', title: res['PATIENT-IMPORT-STREET'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-STREET', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactStreetLineAdditional', title: res['PATIENT-IMPORT-ADDRESS-LINE-2'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-ADDRESS-LINE-2', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactCity', title: res['PATIENT-IMPORT-CITY'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-CITY', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactCounty', title: res['PATIENT-IMPORT-COUNTY'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-COUNTY', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactState', title: res['PATIENT-IMPORT-STATE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-STATE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactPostalCode', title: res['PATIENT-IMPORT-POSTAL-CODE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-POSTAL-CODE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactCountry', title: res['PATIENT-IMPORT-COUNTRY'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-COUNTRY', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactRole', title: res['PATIENT-IMPORT-ROLE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-ROLE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactSubRole', title: res['PATIENT-IMPORT-SUB-ROLE'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-SUB-ROLE', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactExtensions', title: res['PATIENT-IMPORT-EXTENSIONS'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-EXTENSIONS', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactDescription', title: res['PATIENT-IMPORT-DESCRIPTION'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-DESCRIPTION', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactFrom', title: res['PATIENT-IMPORT-FROM'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-FROM', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          {
            field: 'contactTo', title: res['PATIENT-IMPORT-TO'].replace('{type}',
              res['PATIENT-IMPORT-TYPE-CONTACT']), titleKeys: ['PATIENT-IMPORT-TO', 'PATIENT-IMPORT-TYPE-CONTACT'], hidden: true
          },
          // Insurance
          { field: 'insurerName', title: res['PATIENT-IMPORT-INSURER-NAME'], titleKeys: ['PATIENT-IMPORT-INSURER-NAME'], hidden: true },
          {
            field: 'insuranceSubjectId', title: res['PATIENT-IMPORT-INSURANCE-SUBJECT-ID'],
            titleKeys: ['PATIENT-IMPORT-INSURANCE-SUBJECT-ID'], hidden: true
          },
          {
            field: 'insurancePlanId', title: res['PATIENT-IMPORT-INSURANCE-PLAN-ID'],
            titleKeys: ['PATIENT-IMPORT-INSURANCE-PLAN-ID'], hidden: true
          },
          {
            field: 'insuranceGroupId', title: res['PATIENT-IMPORT-INSURANCE-GROUP-ID'],
            titleKeys: ['PATIENT-IMPORT-INSURANCE-GROUP-ID'], hidden: true
          },
          {
            field: 'insuranceStartDate', title: res['PATIENT-IMPORT-INSURANCE-START-DATE'],
            titleKeys: ['PATIENT-IMPORT-INSURANCE-START-DATE'], hidden: true
          },
          {
            field: 'insuranceEndDate', title: res['PATIENT-IMPORT-INSURANCE-END-DATE'],
            titleKeys: ['PATIENT-IMPORT-INSURANCE-END-DATE'], hidden: true
          },
          {
            field: 'insuredFirstName', title: res['PATIENT-IMPORT-INSURED-FIRST-NAME'],
            titleKeys: ['PATIENT-IMPORT-INSURED-FIRST-NAME'], hidden: true
          },
          {
            field: 'insuredLastName', title: res['PATIENT-IMPORT-INSURED-LAST-NAME'],
            titleKeys: ['PATIENT-IMPORT-INSURED-LAST-NAME'], hidden: true
          },
          {
            field: 'policyHolder', title: res['PATIENT-IMPORT-POLICY-HOLDER'],
            titleKeys: ['PATIENT-IMPORT-POLICY-HOLDER'], hidden: true
          },
          {
            field: 'insurerPhoneNumber', title: res['PATIENT-IMPORT-INSURER-PHONE-NUMBER'],
            titleKeys: ['PATIENT-IMPORT-INSURER-PHONE-NUMBER'], hidden: true
          },
          {
            field: 'insurerWebSite', title: res['PATIENT-IMPORT-INSURER-WEBSITE'],
            titleKeys: ['PATIENT-IMPORT-INSURER-WEBSITE'], hidden: true
          },
          { field: 'employer', title: res['PATIENT-IMPORT-EMPLOYER'], titleKeys: ['PATIENT-IMPORT-EMPLOYER'], hidden: true },
          {
            field: 'insuranceExternalId', title: res['PATIENT-IMPORT-INSURANCE-EXTERNAL-ID'],
            titleKeys: ['PATIENT-IMPORT-INSURANCE-EXTERNAL-ID'], hidden: true
          }, 
        ];

        // If there is a previously saved grid settings, use it.
        const gridSettings: GridSettings = this.gridStatePersistingService.get('grid-settings-subject-import');
        if (gridSettings !== null)
        {
          const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
            JSON.stringify(gridSettings.columnsConfig), this.view.columnsConfig);
          this.view.columnsConfig = loadedColumns;

          if (gridSettings.state && gridSettings.state.filter && gridSettings.state.filter.filters)
          {
            GridQueryUtils.convertFiltersToJSDates(gridSettings.state.filter.filters, this.view.columnsConfig);
          }
          this.view.state = gridSettings.state;
        }
      }
    });
  }

  /**
   * Persit grid columns into storage.
   */
  private saveGrid(): void
  {
    const gridConfig: GridSettings = { columnsConfig: this.view.columnsConfig, state: this.view.state };
    this.gridStatePersistingService.set('grid-settings-subject-import', gridConfig);
  }

}
